import {
  DEFAULT_TIMEZONE,
  IMPORT_TEMPLATE_COLUMNS,
  suggestMapping,
  toWallInput,
} from '@performance-crm/shared';
// Solo tipos: el módulo real se carga bajo demanda con `await import('xlsx')` para no engordar el bundle inicial.
import type * as XlsxModule from 'xlsx';

/** Extensiones que se leen como hoja de cálculo (SheetJS, cargado bajo demanda); el resto se trata como CSV. */
const SHEET_EXT = /\.(xlsx|xlsm|xls|ods)$/i;

export function isSpreadsheet(name: string): boolean {
  return SHEET_EXT.test(name);
}

const csvCell = (v: string) => (/[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

export interface SheetFile {
  csv: string;
  /** Hoja leída (null para un CSV). */
  sheetName: string | null;
  /** Todas las hojas del libro (vacío para un CSV); si hay más de una, el usuario puede cambiarla. */
  sheetNames: string[];
  /**
   * Separador decimal con que viajan las cifras: «.» para una hoja de cálculo (sus celdas numéricas se serializan
   * siempre con punto, así «45.125» son 45,125 y no 45 125); null para un CSV, donde la API lo infiere del
   * separador de columnas.
   */
  decimal: '.' | null;
}

type Xlsx = typeof XlsxModule;
type Sheet = XlsxModule.WorkSheet;

const hasContent = (r: unknown[]) => r.some((c) => c !== '' && c !== null && c !== undefined);
const sheetRows = (XLSX: Xlsx, ws: Sheet) =>
  XLSX.utils
    .sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: '', blankrows: false })
    .filter(hasContent);

/**
 * Elige la hoja a importar: la pedida, o la que más columnas reconocidas tiene entre las que traen cabecera y
 * al menos una fila (así una hoja "Instrucciones" al inicio del libro no gana); en su defecto, la primera.
 */
function pickSheet(XLSX: Xlsx, wb: XlsxModule.WorkBook, wanted?: string): string {
  if (wanted && wb.SheetNames.includes(wanted)) return wanted;
  let best: { name: string; score: number } | null = null;
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    if (!ws) continue;
    const rows = sheetRows(XLSX, ws);
    if (rows.length < 2) continue;
    const headers = (rows[0] ?? []).map((c) => String(c ?? '').trim()).filter(Boolean);
    const score = Object.keys(suggestMapping(headers)).length;
    if (!best || score > best.score) best = { name, score };
  }
  return best?.name ?? wb.SheetNames[0]!;
}

/**
 * Texto de un archivo de texto (CSV) con la codificación detectada: UTF-8 si es válido y, si no, Windows-1252
 * (el «CSV delimitado por comas» que guarda Excel en Windows viene en ANSI y `file.text()` convertiría «código» en
 * «c�digo», con lo que la cabecera no se reconocería).
 */
export async function readTextFile(file: File): Promise<string> {
  const bytes = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

/**
 * Convierte una hoja de un Excel a CSV. Las fechas se serializan como dd/mm/aaaa hh:mm (formato que entiende
 * `parseCapturedAt`) y los números como texto con punto decimal y sin notación científica, así celulares y DNI
 * llegan tal cual (`decimal: '.'` se lo dice a la API). Se emiten **todas las filas desde la 1**, las vacías como
 * líneas en blanco (menos las del final): así la fila N del CSV es la fila N que la persona ve en Excel y los
 * errores de una carga se pueden ubicar; el lector de CSV las salta. Un CSV se devuelve como texto sin tocar
 * (salvo la codificación, que se detecta con `readTextFile`).
 */
export async function fileToCsv(file: File, sheet?: string): Promise<SheetFile> {
  if (!isSpreadsheet(file.name))
    return { csv: await readTextFile(file), sheetName: null, sheetNames: [], decimal: null };
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: true });
  if (wb.SheetNames.length === 0) throw new Error('El archivo no tiene hojas');
  const sheetName = pickSheet(XLSX, wb, sheet);
  const ws = wb.Sheets[sheetName]!;
  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    raw: true,
    defval: '',
    blankrows: true,
    range: 0,
  });
  while (rows.length && !hasContent(rows[rows.length - 1]!)) rows.pop();
  const text = (v: unknown): string => {
    if (v === null || v === undefined) return '';
    if (v instanceof Date) {
      if (Number.isNaN(v.getTime())) return '';
      const p = (n: number) => String(n).padStart(2, '0');
      const hasTime = v.getHours() !== 0 || v.getMinutes() !== 0;
      return `${p(v.getDate())}/${p(v.getMonth() + 1)}/${v.getFullYear()}${hasTime ? ` ${p(v.getHours())}:${p(v.getMinutes())}` : ''}`;
    }
    if (typeof v === 'number') return Number.isInteger(v) ? v.toFixed(0) : String(v);
    return String(v).trim();
  };
  const lines = rows.map((r) => (hasContent(r) ? r.map(text).map(csvCell).join(',') : ''));
  return { csv: lines.join('\n'), sheetName, sheetNames: wb.SheetNames, decimal: '.' };
}

/** Descarga la plantilla oficial (encabezados reconocidos + 2 filas de ejemplo + hoja de instrucciones). */
export async function downloadLeadTemplate(fileName = 'plantilla-leads.xlsx'): Promise<void> {
  const XLSX = await import('xlsx');
  const headers = IMPORT_TEMPLATE_COLUMNS.map((c) => c.header);
  const example = IMPORT_TEMPLATE_COLUMNS.map((c) => c.example);
  const example2 = IMPORT_TEMPLATE_COLUMNS.map((c) =>
    c.field === 'firstName'
      ? 'Carlos'
      : c.field === 'lastNamePaternal'
        ? 'Ramos'
        : c.field === 'phone'
          ? '912345678'
          : c.field === 'email' || c.field === 'documentNumber' || c.field === 'note'
            ? ''
            : c.example,
  );
  const ws = XLSX.utils.aoa_to_sheet([headers, example, example2]);
  ws['!cols'] = headers.map((h) => ({ wch: Math.max(14, h.length + 4) }));
  // Celular y DNI como texto para que Excel no los convierta a número (perdería ceros a la izquierda).
  for (const rowIdx of [1, 2])
    IMPORT_TEMPLATE_COLUMNS.forEach((c, colIdx) => {
      if (c.field !== 'phone' && c.field !== 'documentNumber') return;
      const addr = XLSX.utils.encode_cell({ r: rowIdx, c: colIdx });
      const cell = ws[addr] as { t?: string; v?: unknown } | undefined;
      if (cell && cell.v !== '') cell.t = 's';
    });
  const help = XLSX.utils.aoa_to_sheet([
    ['Columna', 'Obligatoria', 'Cómo llenarla'],
    ...IMPORT_TEMPLATE_COLUMNS.map((c) => [c.header, c.required ? 'Sí' : 'No', c.hint]),
    [],
    [
      'Identidad',
      '',
      'Cada fila se compara con la base por DNI → celular → correo. Si la persona ya existe, es un recontacto.',
    ],
    [
      'Recontacto',
      '',
      'Con un lead activo en el mismo proyecto no se crea otro: se anota en el existente y conserva a su asesor (si estaba en bolsa, lo toma el asesor indicado). Si su lead anterior estaba cerrado o era de otro proyecto, se crea un lead nuevo como recontacto.',
    ],
    [
      'Asesor',
      '',
      'Prioridad: columna "Asesor" → asesor por defecto de la importación → reparto por turnos del proyecto.',
    ],
  ]);
  help['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 110 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Leads');
  XLSX.utils.book_append_sheet(wb, help, 'Instrucciones');
  XLSX.writeFile(wb, fileName);
}

/**
 * Fecha real de Excel (ux-transversal-12): un `Date` cuyos componentes locales son la hora de pared de la zona de
 * la empresa, con el formato con que Excel la muestra. SheetJS escribe el `Date` con la hora local del navegador,
 * por eso se arma desde la pared y no desde el instante.
 */
export class ExcelDate {
  readonly value: Date;
  constructor(
    iso: string,
    readonly withTime: boolean,
    tz: string = DEFAULT_TIMEZONE,
  ) {
    const wall = toWallInput(new Date(iso), tz);
    this.value = new Date(
      Number(wall.slice(0, 4)),
      Number(wall.slice(5, 7)) - 1,
      Number(wall.slice(8, 10)),
      withTime ? Number(wall.slice(11, 13)) : 0,
      withTime ? Number(wall.slice(14, 16)) : 0,
    );
  }
  get format() {
    return this.withTime ? 'dd/mm/yyyy hh:mm' : 'dd/mm/yyyy';
  }
}
/** Fecha y hora (dd/mm/aaaa hh:mm) o solo fecha (dd/mm/aaaa) en la zona de la empresa; vacío si no hay valor. */
export const excelDateTime = (iso: string | null | undefined, tz?: string) =>
  iso ? new ExcelDate(iso, true, tz) : null;
export const excelDate = (iso: string | null | undefined, tz?: string) =>
  iso ? new ExcelDate(iso, false, tz) : null;

export type SheetCell = string | number | ExcelDate | null | undefined;

/** Descarga un libro con varias hojas (cabecera + filas); los anchos se ajustan al contenido. */
export async function downloadWorkbook(
  sheets: Array<{ name: string; header: string[]; rows: SheetCell[][] }>,
  fileName: string,
): Promise<void> {
  const XLSX = await import('xlsx');
  const wb = XLSX.utils.book_new();
  for (const sheet of sheets) {
    const ws = XLSX.utils.aoa_to_sheet(
      [
        sheet.header,
        ...sheet.rows.map((r) =>
          r.map((c) => (c === undefined ? null : c instanceof ExcelDate ? c.value : c)),
        ),
      ],
      { cellDates: true },
    );
    // Formato de las celdas de fecha (SheetJS no lo infiere): así Excel las ordena y filtra como fechas.
    sheet.rows.forEach((r, ri) =>
      r.forEach((c, ci) => {
        if (!(c instanceof ExcelDate)) return;
        const cell = ws[XLSX.utils.encode_cell({ r: ri + 1, c: ci })] as { z?: string } | undefined;
        if (cell) cell.z = c.format;
      }),
    );
    ws['!cols'] = sheet.header.map((h, i) => ({
      wch: Math.min(
        60,
        Math.max(
          h.length + 2,
          ...sheet.rows.map((r) => {
            const c = r[i];
            return (c instanceof ExcelDate ? c.format.length : String(c ?? '').length) + 2;
          }),
        ),
      ),
    }));
    XLSX.utils.book_append_sheet(wb, ws, sheet.name.slice(0, 31));
  }
  XLSX.writeFile(wb, fileName);
}
